import { ButtonBase, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Typography from '~components/typography';

const useStyles = makeStyles(
  (theme) => ({
    root: { marginBottom: 12 },

    tabRoot: { ...theme.flex.crossCenter },

    tab: {
      display: 'block',
      marginRight: '12px',
      color: theme.colors.grey66,

      '&:hover $tabContent': {
        color: theme.colors.brandAction,
      },
    },

    tabContentActive: {
      color: theme.colors.brandAction,
    },

    tabContent: {
      padding: '0px 8px',
    },

    bottomDivider: {
      position: 'absolute',
      display: 'inline-flex',
      height: '0px',
      backgroundColor: theme.colors.white,
      width: '56px',
    },

    bottomDividerActive: {
      backgroundColor: theme.colors.brandIcon,
    },

    sliderContainer: {
      position: 'relative',
      width: '100%',
      marginTop: '8px',
      borderRadius: '99px',
      height: ({ sliderContainerHeight }) => sliderContainerHeight || '4px',
      backgroundColor: ({ sliderContainerColor }) =>
        theme.colors[sliderContainerColor] ?? sliderContainerColor ?? 'transparent',
    },

    blueSliderBlock: {
      display: 'block',
      position: 'absolute',
      width: '56px',
      height: '4px',
      borderRadius: '99px',
      backgroundColor: theme.colors.brandIcon,
      left: 0,
      transition: '0.3s',
    },
  }),
  { name: 'SliderTab' }
);

function SliderTab({
  onClickTab = () => {},
  tabs,
  leftWidthCalc = 185,
  sliderContainerColor,
  sliderContainerHeight,
  defaultTabValue,
}) {
  const classes = useStyles({ sliderContainerColor, sliderContainerHeight });
  const switchTabRef = useRef({});

  const [selectedSwitchTab, setSelectedSwitchTab] = useState(defaultTabValue || tabs[0]?.value);
  const [blueBlockPosition, setBlueBlockPosition] = useState(3);
  const [isNeedRePostionBlueBlock, setIsNeedRePostionBlueBlock] = useState(true);

  const handleClickSwitchChange = (selectedItem) => {
    if (selectedItem === selectedSwitchTab) return false;

    setBlueBlockPosition(
      switchTabRef.current[selectedItem].getBoundingClientRect().left - leftWidthCalc
    );

    setTimeout(() => {
      setSelectedSwitchTab(selectedItem);
      onClickTab(selectedItem);
    }, 300);
  };

  useEffect(() => {
    if (isNeedRePostionBlueBlock && defaultTabValue && defaultTabValue !== tabs[0]?.value) {
      setIsNeedRePostionBlueBlock(false);
      setBlueBlockPosition(
        switchTabRef.current[defaultTabValue].getBoundingClientRect().left - leftWidthCalc
      );
    }
  }, [defaultTabValue, isNeedRePostionBlueBlock, leftWidthCalc, tabs]);

  const renderTab = (item) => (
    <ButtonBase
      className={classes.tab}
      disableRipple
      onClick={() => {
        handleClickSwitchChange(item?.value);
      }}
      key={item?.value}
    >
      <Typography
        variant="h6"
        className={classNames(classes.tabContent, {
          [classes.tabContentActive]: selectedSwitchTab === item?.value,
        })}
      >
        {item?.label}
      </Typography>

      <span
        className={classNames(classes.bottomDivider, {
          [classes.bottomDividerActive]: selectedSwitchTab === item?.value,
        })}
        ref={(element) => (switchTabRef.current[item?.value] = element)}
      />
    </ButtonBase>
  );

  const renderSlider = () => (
    <div className={classes.sliderContainer}>
      <span className={classes.blueSliderBlock} style={{ left: blueBlockPosition }} />
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.tabRoot}>{tabs.map((tab) => renderTab(tab))}</div>
      {renderSlider()}
    </div>
  );
}

SliderTab.prototype = {
  onClickTab: PropTypes.func.isRequired,
  tabs: PropTypes.array.isRequired,
  leftWidthCalc: PropTypes.number,
};

export default SliderTab;
